import React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { withStyles } from '@mui/styles'
import { Typography, Tooltip } from '@mui/material'

import { useWidth } from '../../../../core/utils'
import { publicLayoutStyles } from './public-layout.styles'
import { FOOTER_LINKS, CONTACT_PHONE, DEFAULT_ROUTE, APP_VERSION_MSSG } from '../constants'
import { ReactComponent as LogoNedgiaFull } from '../../../../resources/images/logo_nedgia_full.svg'
import { ReactComponent as LogoNedgia } from '../../../../resources/images/logo_nedgia.svg'

/**
 * @typedef {object} PublicLayoutComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {React.ReactNode} children - Contenido a renderizar en el cuerpo del layout.
 */

/**
 * Layout para áreas públicas (no autenticadas) de la aplicación.
 *
 * Proporciona un layout completo con header (logo y contacto), cuerpo principal, y footer con
 * enlaces legales/corporativos. Se adapta a móviles cambiando el logo y reorganizando elementos.
 * Utiliza react-intl para textos internacionalizados.
 *
 * @component
 * @example
 * <PublicLayoutComponent>
 *   <LoginPage />
 * </PublicLayoutComponent>
 *
 * @param {PublicLayoutComponentProps} props - Las props del componente PublicLayoutComponent.
 */
export const PublicLayoutComponent = withStyles(publicLayoutStyles)(({ children, classes }) => {
  let { formatMessage } = useIntl()
  let screenWidth = useWidth()
  const isMobileWidth = screenWidth === 'xs'

  return (
    <div className={classes.root}>
      <div className={classes.headerWrapper}>
        <div className={classes.headerLeftContent}>
          <Tooltip title={APP_VERSION_MSSG}>
            <Link to={DEFAULT_ROUTE}>{isMobileWidth ? <LogoNedgia /> : <LogoNedgiaFull />}</Link>
          </Tooltip>
        </div>
        <div className={classes.headerRightContent}>
          <Typography variant="h6">
            {formatMessage({ id: 'layout.public.header.right.upper.label' })}
          </Typography>
          <Typography variant="body1">
            {formatMessage(
              { id: 'layout.public.header.right.bottom.label' },
              { phone: CONTACT_PHONE }
            )}
          </Typography>
        </div>
      </div>
      <div className={classes.bodyWrapper}>{children}</div>
      <div className={classes.footerWrapper}>
        <div className={classes.footerContent}>
          <Typography variant="body1" paragraph>
            {FOOTER_LINKS.map((item, i) => (
              <span key={i}>
                <span className={classes.footerLink} onClick={() => window.open(item.link)}>
                  {formatMessage({ id: item.id })}
                </span>
                {i !== FOOTER_LINKS.length - 1 && '|'}
              </span>
            ))}
          </Typography>
          <Typography variant="body1">
            {formatMessage({ id: 'layout.public.footer.bottom.label' })}
          </Typography>
        </div>
      </div>
    </div>
  )
})
